/**
 * KAN-874: общий список "Требуют внимания" мобильной доски: чистые функции.
 * Ответ доски с ?attention=1 отдаёт не больше 50 карточек на колонку и ни одной
 * у свёрнутого этапа, поэтому такие этапы догружаются порциями ?stage=
 * (attentionTailStages), а затем всё сводится в один список с подписью этапа
 * (attentionListItems).
 */
import { NO_STAGE_TITLE, sameStage } from '@/lib/crmPipeline';
import type { CrmPipelineCard, CrmPipelineResponse, CrmStageFilter } from '@/types/crmPipeline';

export interface AttentionListItem {
  card: CrmPipelineCard;
  /** Название колонки этапа ("Без этапа", если этапа нет или он неизвестен). */
  stageTitle: string;
}

/** Этапы, которые надо догрузить: в ответе доски не все их клиенты. */
export function attentionTailStages(data: CrmPipelineResponse): CrmStageFilter[] {
  const tails: CrmStageFilter[] = data.stages
    .filter((stage) => {
      if (stage.count <= 0) return false;
      if (stage.collapsed) return true;
      return stage.count > data.cards.filter((c) => sameStage(c.stage_id, stage.id)).length;
    })
    .map((stage) => stage.id);
  const loose = data.cards.filter((c) => !data.stages.some((s) => sameStage(c.stage_id, s.id))).length;
  if (data.no_stage_count > loose) tails.push('none');
  return tails;
}

const time = (value: string | null): number => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortKey = (card: CrmPipelineCard): number => time(card.attention_since) || time(card.last_activity_at);

/** KAN-919: ранг backend по возрастанию (без ранга в конце), при равном ранге свежее выше. */
const byAttentionOrder = (a: CrmPipelineCard, b: CrmPipelineCard): number => {
  const rankA = a.attention_rank ?? Number.POSITIVE_INFINITY;
  const rankB = b.attention_rank ?? Number.POSITIVE_INFINITY;
  if (rankA !== rankB) return rankA < rankB ? -1 : 1;
  return sortKey(b) - sortKey(a);
};

/** Карточки доски и хвостов одним списком: без повторов, только внимание, в порядке backend (KAN-919). */
export function attentionListItems(
  data: CrmPipelineResponse,
  tails: readonly CrmPipelineCard[]
): AttentionListItem[] {
  const unique = new Map<string, CrmPipelineCard>();
  [...data.cards, ...tails].forEach((card) => {
    if (card.attention) unique.set(card.id, card);
  });
  const titleOf = (card: CrmPipelineCard) =>
    data.stages.find((s) => sameStage(card.stage_id, s.id))?.name ?? NO_STAGE_TITLE;
  return [...unique.values()]
    .sort(byAttentionOrder)
    .map((card) => ({ card, stageTitle: titleOf(card) }));
}
